// =============================================================================
// TeamOutingsScreen — Teamuitjes (work item team-uitjes)
// =============================================================================
// Quote requests from /teams-v2 (Rick's spec "Teamuitje: reserveren via
// offerte", 2026-10-21). A request blocks its time slots for 48 hours; the
// customer accepts by mail; NEOSEUM invoices by hand. Here staff see every
// request with its status and pay-by date and move it on by hand:
//   Gefactureerd (with the invoice date: the pay-by date is recomputed from it),
//   Betaald (books an 'invoice' payment on the booking),
//   Annuleren (frees the time slots; no refund logic).
// Verlopen happens automatically after 48 hours (booking-team-tick).
//
// Nieuw teamuitje (Dirk, 2026-10-30): staff create one by hand for a request
// by phone or mail, as a quote (accept link, 48 h hold) or confirmed at once,
// without the website's 7-day minimum, group limits and open-quote limit, with
// an editable price per slot and optionally no customer mail. A requested
// quote can be confirmed here too (Bevestigen). Both go through booking-team
// (admin_create / admin_accept), which sends the customer mails.
//
// Gated by booking_team_outings (route + RPCs).
import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { formatError } from '../lib/errors';
import { useLocationFilter } from '../lib/location-context';
import { formatVenueDate, formatVenueDateTime, formatVenueTime } from '../lib/time';
import {
  confirmTeamQuote,
  createTeamQuote,
  euro,
  fetchTeamConfig,
  fetchTeamSlots,
  freeStarts,
  listTeamQuotes,
  plainDate,
  TEAM_STATUS_CLASS,
  TEAM_STATUS_LABEL,
  updateTeamQuote,
  venueToday,
  type TeamLocationConfig,
  type TeamQuote,
  type TeamSlot,
} from '../lib/teamQuotes';

const FILTERS: Array<{ value: string; label: string }> = [
  { value: 'open', label: 'Open (aangevraagd, geaccepteerd, gefactureerd)' },
  { value: '', label: 'Alles' },
  { value: 'requested', label: 'Aangevraagd' },
  { value: 'accepted', label: 'Geaccepteerd' },
  { value: 'invoiced', label: 'Gefactureerd' },
  { value: 'paid', label: 'Betaald' },
  { value: 'expired', label: 'Verlopen' },
  { value: 'cancelled', label: 'Geannuleerd' },
];

const MAIL_LABEL: Record<string, string> = {
  quote: 'Offerte',
  confirmation: 'Bevestiging',
  reminder: 'Herinnering',
  expired: 'Verloopmail',
  pay_by: 'Betaaldatum-melding',
};

export function TeamOutingsScreen() {
  const { locationId } = useLocationFilter();
  const [params, setParams] = useSearchParams();
  const [filter, setFilter] = useState('open');
  const [rows, setRows] = useState<TeamQuote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const openId = params.get('q');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await listTeamQuotes(locationId, filter || null));
      setError(null);
    } catch (e) {
      setError(formatError(e));
    } finally {
      setLoading(false);
    }
  }, [locationId, filter]);

  useEffect(() => {
    void load();
  }, [load]);

  // A link from the mails (?q=<id>) opens that request even when the filter
  // would hide it.
  useEffect(() => {
    if (openId && !loading && !rows.some((r) => r.id === openId) && filter !== '') {
      setFilter('');
    }
  }, [openId, loading, rows, filter]);

  function toggle(id: string) {
    const next = new URLSearchParams(params);
    if (openId === id) next.delete('q');
    else next.set('q', id);
    setParams(next, { replace: true });
  }

  async function act(q: TeamQuote, fn: () => Promise<TeamQuote>) {
    setBusyId(q.id);
    try {
      const updated = await fn();
      setRows((rs) => rs.map((r) => (r.id === q.id ? updated : r)));
      setError(null);
    } catch (e) {
      setError(formatError(e));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="screen payments-screen team-screen">
      <h1 className="payments-title">Teamuitjes</h1>

      <div className="payments-filters">
        <label className="walkin-field payments-filter">
          <span>Status</span>
          <select value={filter} onChange={(e) => setFilter(e.target.value)}>
            {FILTERS.map((f) => (
              <option key={f.value} value={f.value}>{f.label}</option>
            ))}
          </select>
        </label>
        <div className="runs-actions">
          <button className="btn-link" onClick={() => void load()}>Ververs</button>
          <button className="btn-primary" onClick={() => setCreating(true)}>Nieuw teamuitje</button>
        </div>
      </div>

      {error ? <div className="error-card">{error}</div> : null}
      {loading ? <div className="muted">Laden…</div> : null}
      {!loading && rows.length === 0 ? (
        <div className="muted vouchers-empty">Geen aanvragen.</div>
      ) : null}

      {rows.length > 0 ? (
        <table className="payments-table team-table">
          <thead>
            <tr>
              <th>Datum</th>
              <th>Bedrijf</th>
              <th>Groep</th>
              <th>Totaal incl. btw</th>
              <th>Status</th>
              <th>Uiterste betaaldatum</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((q) => (
              <TeamRow
                key={q.id}
                q={q}
                open={openId === q.id}
                busy={busyId === q.id}
                onToggle={() => toggle(q.id)}
                onAct={(fn) => void act(q, fn)}
              />
            ))}
          </tbody>
        </table>
      ) : null}

      {creating ? (
        <NewTeamOutingModal
          defaultLocationId={locationId}
          onClose={(created) => {
            setCreating(false);
            if (created) {
              void load();
              const next = new URLSearchParams(params);
              next.set('q', created.id);
              setParams(next, { replace: true });
            }
          }}
        />
      ) : null}
    </div>
  );
}

// " door <operator>" for the timeline; empty when unknown (older rows).
function by(who: string | null | undefined): string {
  return who ? ` door ${who}` : '';
}

function payByLate(q: TeamQuote): boolean {
  return !!q.pay_by_date && (q.status === 'accepted' || q.status === 'invoiced') &&
    q.pay_by_date <= venueToday();
}

function TeamRow({
  q,
  open,
  busy,
  onToggle,
  onAct,
}: {
  q: TeamQuote;
  open: boolean;
  busy: boolean;
  onToggle: () => void;
  onAct: (fn: () => Promise<TeamQuote>) => void;
}) {
  const [invoiceDate, setInvoiceDate] = useState(() => q.invoice_date ?? venueToday());
  const [editDate, setEditDate] = useState(false);
  const [notes, setNotes] = useState(q.staff_notes ?? '');

  const canConfirm = q.status === 'requested';
  const canPay = q.status === 'accepted' || q.status === 'invoiced';
  const canCancel = q.status === 'requested' || q.status === 'accepted' || q.status === 'invoiced';

  function cancel() {
    const msg = q.status === 'requested'
      ? `Aanvraag ${q.quote_number} intrekken? De tijdsloten komen direct vrij. De klant krijgt geen mail.`
      : `Reservering ${q.quote_number} annuleren? De tijdsloten komen vrij. Er wordt niets terugbetaald en de klant krijgt geen mail.`;
    const reason = window.prompt(`${msg}\n\nReden:`);
    if (reason === null) return;
    onAct(() => updateTeamQuote(q.id, 'cancelled', { reason: reason.trim() || null }));
  }

  function confirmNow() {
    const mail = q.mail_customer === false ? 'De klant krijgt geen mail.' : 'De klant krijgt de bevestigingsmail.';
    if (!window.confirm(`${q.quote_number} (${q.company}) bevestigen namens de klant? De reservering is dan definitief. ${mail}`)) return;
    onAct(() => confirmTeamQuote(q.id));
  }

  function paid() {
    if (!window.confirm(`${q.quote_number} (${q.company}) op Betaald zetten? Bedrag ${euro(q.total_inc_cents)}.`)) return;
    onAct(() => updateTeamQuote(q.id, 'paid'));
  }

  return (
    <>
      <tr className={`team-row${open ? ' open' : ''}`} onClick={onToggle} style={{ cursor: 'pointer' }}>
        <td>
          {formatVenueDate(q.start_at)}<br />
          <span className="muted">{formatVenueTime(q.start_at)}–{formatVenueTime(q.end_at)} · {q.location.name}</span>
        </td>
        <td>
          {q.company}<br />
          <span className="muted">{q.quote_number}</span>
        </td>
        <td>{q.group_size} pers.<br /><span className="muted">{q.slot_count} × {q.places_per_slot} plekken</span></td>
        <td>{euro(q.total_inc_cents)}<br /><span className="muted">{euro(q.total_ex_cents)} excl.</span></td>
        <td>
          <span className={`voucher-pill ${TEAM_STATUS_CLASS[q.status]}`}>{TEAM_STATUS_LABEL[q.status]}</span>
          {q.status === 'requested' ? (
            <><br /><span className="muted">verloopt {formatVenueDateTime(q.expires_at)}</span></>
          ) : null}
        </td>
        <td className={payByLate(q) ? 'team-late' : undefined}>
          {q.status === 'requested' || q.status === 'expired' || q.status === 'cancelled'
            ? '—'
            : plainDate(q.pay_by_date)}
          {q.status === 'accepted' ? <><br /><span className="muted">bij factuur vandaag</span></> : null}
        </td>
      </tr>
      {open ? (
        <tr className="team-detail">
          <td colSpan={6}>
            <div className="team-detail-grid">
              <div>
                <h3>Contact</h3>
                <div>{q.contact_name}</div>
                <div><a href={`mailto:${q.email}`}>{q.email}</a></div>
                <div><a href={`tel:${q.phone}`}>{q.phone}</a></div>
                <div className="muted">Taal: {q.locale.toUpperCase()}</div>
              </div>
              <div>
                <h3>Factuur</h3>
                <div>{q.company}</div>
                <div>{q.street}</div>
                <div>{q.postcode} {q.city}</div>
                {q.invoice_email ? <div>Factuur-e-mail: {q.invoice_email}</div> : null}
                {q.vat_number ? <div>Btw-nummer: {q.vat_number}</div> : null}
                {q.reference ? <div>Referentie: {q.reference}</div> : null}
                <div className="muted">
                  {q.slot_count} × {euro(q.price_per_slot_ex_cents)} = {euro(q.total_ex_cents)} excl.
                  + btw {q.vat_rate_pct}% {euro(q.vat_cents)} = {euro(q.total_inc_cents)}
                </div>
              </div>
              <div>
                <h3>Schema</h3>
                {q.parts.map((p, i) => (
                  <div key={p.slot_start}>
                    {formatVenueTime(p.slot_start)}{q.parts.length > 1 ? ` · groep ${i + 1}` : ''} · {p.people} pers.
                  </div>
                ))}
                <div className="muted">
                  Boeking <Link to={`/booking/${q.booking_number}`}>{q.booking_number}</Link>
                </div>
              </div>
              <div>
                <h3>Verloop</h3>
                <div className="muted">
                  {q.created_by
                    ? `Handmatig aangemaakt ${formatVenueDateTime(q.created_at)}${by(q.created_by)}`
                    : `Aangevraagd ${formatVenueDateTime(q.created_at)}`}
                </div>
                {q.mail_customer === false ? <div className="muted">Geen mail naar klant</div> : null}
                {q.reminder_sent_at ? <div className="muted">Herinnering {formatVenueDateTime(q.reminder_sent_at)}</div> : null}
                {q.accepted_at ? (
                  <div className="muted">
                    {q.accepted_by ? 'Bevestigd' : 'Geaccepteerd door klant'} {formatVenueDateTime(q.accepted_at)}{by(q.accepted_by)}
                  </div>
                ) : null}
                {q.expired_at ? <div className="muted">Verlopen {formatVenueDateTime(q.expired_at)}</div> : null}
                {q.invoiced_at ? (
                  <div className="muted">
                    Gefactureerd {formatVenueDateTime(q.invoiced_at)}{by(q.invoiced_by)}
                  </div>
                ) : null}
                {(q.history ?? []).filter((h) => h.what === 'invoice_date').map((h) => (
                  <div key={h.at} className="muted">
                    Factuurdatum {plainDate(h.was ?? null)} → {plainDate(h.invoice_date ?? null)}, {formatVenueDateTime(h.at)}{by(h.by)}
                  </div>
                ))}
                {q.invoice_date ? <div className="muted">Factuurdatum {plainDate(q.invoice_date)}</div> : null}
                {q.paid_at ? <div className="muted">Betaald {formatVenueDateTime(q.paid_at)}{by(q.paid_by)}</div> : null}
                {q.cancelled_at ? (
                  <div className="muted">
                    Geannuleerd {formatVenueDateTime(q.cancelled_at)}{by(q.cancelled_by)}{q.cancel_reason ? `: ${q.cancel_reason}` : ''}
                  </div>
                ) : null}
                {q.mail_log.filter((m) => !m.ok).map((m) => (
                  <div key={m.at} className="team-late">
                    {MAIL_LABEL[m.kind] ?? m.kind} niet verstuurd ({formatVenueDateTime(m.at)})
                  </div>
                ))}
              </div>
            </div>

            {q.notes ? <p><strong>Opmerkingen klant:</strong> {q.notes}</p> : null}

            <div className="team-actions">
              {canConfirm ? (
                <button className="btn-primary" disabled={busy} onClick={confirmNow}>Bevestigen</button>
              ) : null}
              {q.status === 'accepted' ? (
                // Step 1 after acceptance: the invoice went out. The pay-by date
                // is computed from the invoice date.
                <span className="team-action">
                  <label className="walkin-field">
                    <span>Factuurdatum</span>
                    <input type="date" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} />
                  </label>
                  <button
                    className="btn-primary"
                    disabled={busy || !invoiceDate}
                    onClick={() => onAct(() => updateTeamQuote(q.id, 'invoiced', { invoiceDate }))}
                  >
                    Markeer als gefactureerd
                  </button>
                </span>
              ) : null}
              {q.status === 'invoiced' && !editDate ? (
                <span className="team-action">
                  <span className="muted">Factuurdatum {plainDate(q.invoice_date)}</span>
                  <button className="btn-link" disabled={busy} onClick={() => { setInvoiceDate(q.invoice_date ?? venueToday()); setEditDate(true); }}>
                    wijzigen
                  </button>
                </span>
              ) : null}
              {q.status === 'invoiced' && editDate ? (
                <span className="team-action">
                  <label className="walkin-field">
                    <span>Nieuwe factuurdatum</span>
                    <input type="date" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} />
                  </label>
                  <button
                    className="btn-primary"
                    disabled={busy || !invoiceDate || invoiceDate === q.invoice_date}
                    onClick={() => { setEditDate(false); onAct(() => updateTeamQuote(q.id, 'invoiced', { invoiceDate })); }}
                  >
                    Opslaan
                  </button>
                  <button className="btn-link" onClick={() => setEditDate(false)}>Annuleren</button>
                </span>
              ) : null}
              {canPay ? (
                <button className="btn-primary" disabled={busy} onClick={paid}>Markeer als betaald</button>
              ) : null}
              {canCancel ? (
                <button className="btn-link" disabled={busy} onClick={cancel}>
                  {q.status === 'requested' ? 'Aanvraag intrekken' : 'Annuleren'}
                </button>
              ) : null}
            </div>

            <div className="team-notes">
              <label className="walkin-field">
                <span>Interne notitie</span>
                <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
              </label>
              <button
                className="btn-link"
                disabled={busy || notes === (q.staff_notes ?? '')}
                onClick={() => onAct(() => updateTeamQuote(q.id, 'notes', { staffNotes: notes }))}
              >
                Notitie opslaan
              </button>
            </div>
          </td>
        </tr>
      ) : null}
    </>
  );
}

// Nieuw teamuitje: a request by phone or mail, entered by staff. The start
// times offered are the ones where the group fits in whole free slots
// (booking-team "slots"); the database checks again on save.
function NewTeamOutingModal({
  defaultLocationId,
  onClose,
}: {
  defaultLocationId: string | null;
  onClose: (created: TeamQuote | null) => void;
}) {
  const [config, setConfig] = useState<TeamLocationConfig[] | null>(null);
  const [locationId, setLocationId] = useState('');
  const [date, setDate] = useState('');
  const [group, setGroup] = useState('');
  const [slots, setSlots] = useState<TeamSlot[] | null>(null);
  const [start, setStart] = useState('');
  const [price, setPrice] = useState('');
  const [form, setForm] = useState({
    company: '', contact_name: '', email: '', phone: '', invoice_email: '',
    street: '', postcode: '', city: '', vat_number: '', reference: '', notes: '',
  });
  const [locale, setLocale] = useState<'nl' | 'en'>('nl');
  const [confirm, setConfirm] = useState(false);
  const [noMail, setNoMail] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchTeamConfig()
      .then((locs) => {
        setConfig(locs);
        const first = locs.find((l) => l.location_id === defaultLocationId) ?? locs[0];
        if (first) {
          setLocationId(first.location_id);
          setPrice(euroInput(first.price_per_slot_ex_cents));
        }
      })
      .catch((e) => setError(formatError(e)));
  }, [defaultLocationId]);

  const loc = config?.find((l) => l.location_id === locationId) ?? null;

  useEffect(() => {
    setSlots(null);
    setStart('');
    if (!locationId || !date) return;
    let live = true;
    fetchTeamSlots(locationId, date)
      .then((s) => { if (live) setSlots(s); })
      .catch((e) => { if (live) setError(formatError(e)); });
    return () => { live = false; };
  }, [locationId, date]);

  const n = Number(group);
  const groupOk = Number.isInteger(n) && n >= 1 && n <= 500;
  const starts = slots && groupOk ? freeStarts(slots, n) : [];
  const priceCents = Math.round(Number(price.replace(',', '.')) * 100);
  const priceOk = price.trim() !== '' && Number.isFinite(priceCents) && priceCents >= 0;
  const slotCount = loc && groupOk ? Math.ceil(n / loc.capacity_per_slot) : 0;
  const totalEx = slotCount * (priceOk ? priceCents : 0);
  const totalInc = loc ? Math.round(totalEx * (1 + loc.vat_rate_pct / 100)) : 0;
  const valid = !!loc && !!date && groupOk && starts.includes(start) && priceOk &&
    form.contact_name.trim() !== '' && /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim());

  function field(key: keyof typeof form, label: string, type = 'text') {
    return (
      <label className="walkin-field">
        <span>{label}</span>
        <input
          type={type}
          value={form[key]}
          onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
        />
      </label>
    );
  }

  async function submit() {
    if (!loc) return;
    setBusy(true);
    setError(null);
    try {
      const q = await createTeamQuote({
        location_id: loc.location_id,
        date,
        start,
        group_size: n,
        ...form,
        locale,
        price_per_slot_ex_cents: priceCents,
        mail_customer: !noMail,
        confirm,
      });
      onClose(q);
    } catch (e) {
      setError(formatError(e));
      setBusy(false);
    }
  }

  let startHint = 'Kies…';
  if (!date || !groupOk) startHint = 'Kies eerst datum en groep';
  else if (!slots) startHint = 'Laden…';
  else if (starts.length === 0) startHint = 'Geen vrije starttijd';

  return (
    <div className="dep-modal-backdrop" onClick={() => { /* ✕ only */ }}>
      <div className="dep-modal team-new-modal" onClick={(e) => e.stopPropagation()}>
        <header className="dep-modal-head">
          <div><h2>Nieuw teamuitje</h2></div>
          <button className="btn-link dep-modal-close" onClick={() => onClose(null)} aria-label="Sluiten">✕</button>
        </header>
        <div className="dep-modal-body">
          <form
            className="team-new-form"
            onSubmit={(e) => {
              e.preventDefault();
              if (valid && !busy) void submit();
            }}
          >
            {config && config.length === 0 ? (
              <div className="error-card">Teamuitjes staan voor geen enkele locatie aan.</div>
            ) : null}
            <div className="team-new-grid">
              <label className="walkin-field">
                <span>Locatie</span>
                <select
                  value={locationId}
                  onChange={(e) => {
                    setLocationId(e.target.value);
                    const l = config?.find((c) => c.location_id === e.target.value);
                    if (l) setPrice(euroInput(l.price_per_slot_ex_cents));
                  }}
                >
                  {(config ?? []).map((l) => (
                    <option key={l.location_id} value={l.location_id}>{l.name}</option>
                  ))}
                </select>
              </label>
              <label className="walkin-field">
                <span>Datum</span>
                <input type="date" min={venueToday()} value={date} onChange={(e) => setDate(e.target.value)} />
              </label>
              <label className="walkin-field">
                <span>Aantal personen</span>
                <input type="number" min={1} max={500} value={group} onChange={(e) => setGroup(e.target.value)} />
              </label>
              <label className="walkin-field">
                <span>Starttijd</span>
                <select value={start} onChange={(e) => setStart(e.target.value)} disabled={!slots || !groupOk}>
                  <option value="">{startHint}</option>
                  {starts.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </label>
              <label className="walkin-field">
                <span>Prijs per tijdslot excl. btw (€)</span>
                <input inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} />
              </label>
              <label className="walkin-field">
                <span>Taal klant</span>
                <select value={locale} onChange={(e) => setLocale(e.target.value as 'nl' | 'en')}>
                  <option value="nl">Nederlands</option>
                  <option value="en">Engels</option>
                </select>
              </label>
            </div>
            {loc && groupOk ? (
              <p className="muted">
                {slotCount} {slotCount === 1 ? 'tijdslot' : 'tijdsloten'} × {priceOk ? euro(priceCents) : '—'}
                {' '}= {euro(totalEx)} excl., {euro(totalInc)} incl. {loc.vat_rate_pct}% btw
                {' '}({loc.capacity_per_slot} plekken per tijdslot)
              </p>
            ) : null}

            <div className="team-new-grid">
              {field('company', 'Bedrijf')}
              {field('contact_name', 'Contactpersoon')}
              {field('email', 'E-mail', 'email')}
              {field('phone', 'Telefoon (optioneel)', 'tel')}
              {field('invoice_email', 'Factuur-e-mail (optioneel)', 'email')}
              {field('street', 'Adres (optioneel)')}
              {field('postcode', 'Postcode (optioneel)')}
              {field('city', 'Plaats (optioneel)')}
              {field('vat_number', 'Btw-nummer (optioneel)')}
              {field('reference', 'Referentie (optioneel)')}
            </div>
            <label className="walkin-field">
              <span>Opmerkingen klant (optioneel)</span>
              <textarea rows={2} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
            </label>

            <fieldset className="team-new-mode">
              <label>
                <input type="radio" checked={!confirm} onChange={() => setConfirm(false)} />
                {' '}Offerte sturen: tijdsloten {loc?.quote_hours ?? 48} uur vastgehouden, de klant accepteert via de link
              </label>
              <label>
                <input type="radio" checked={confirm} onChange={() => setConfirm(true)} />
                {' '}Direct bevestigen: de klant heeft al ja gezegd, de reservering is definitief
              </label>
              <label>
                <input type="checkbox" checked={noMail} onChange={(e) => setNoMail(e.target.checked)} />
                {' '}Geen mail naar klant
                {noMail && !confirm ? (
                  <span className="muted"> (de klant krijgt dan ook de acceptatielink niet; bevestig later hier)</span>
                ) : null}
              </label>
            </fieldset>

            {error ? <div className="error-card">{error}</div> : null}
            <button type="submit" className="walkin-primary-btn" disabled={!valid || busy}>
              {busy ? 'Aanmaken…' : confirm ? 'Teamuitje bevestigen' : 'Offerte aanmaken'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

// 21000 -> "210,00" for the price input.
function euroInput(cents: number): string {
  return (cents / 100).toFixed(2).replace('.', ',');
}
